import { type NewsCommentItem } from "@/lib/client/news-comment";
import {
  getNewsCommentAuthorBadge,
  getNewsCommentAuthorName,
} from "@/lib/utils/news-comment-avatar";
import {
  NEWS_REACTION_EMOJIS,
  type NewsReactionEmoji,
  getNewsReactionTargetKey,
} from "@/lib/utils/news-reaction";

export const MAX_COMMENT_LENGTH = 300;
export const DEFAULT_VISIBLE_REPLY_COUNT = 2;
export const DEFAULT_REACTION_DISPLAY_COUNT = 5;

export const COMMENT_SORT_OPTIONS = [
  { value: "recommended", label: "推荐" },
  { value: "newest", label: "从新到旧" },
  { value: "oldest", label: "从旧到新" },
] as const;

export type NewsCommentNode = NewsCommentItem & {
  replies: NewsCommentNode[];
};

export interface FlatReplyItem {
  id: string;
  parentId: string | null;
  authorName: string;
  parentName?: string;
  isParentRootAuthor: boolean;
  content: string;
  createdAt: Date | string;
  isRootAuthor: boolean;
}

export interface CommentThreadItem {
  root: NewsCommentNode;
  rootAuthorName: string;
  rootBadge: ReturnType<typeof getNewsCommentAuthorBadge>;
  rootCreatedLabel: string;
  replies: FlatReplyItem[];
  rootAuthorId: string;
  commentIds: Set<string>;
}

export type ReactionState = { count: number; reacted: boolean };
export type ReactionSummaryMap = Map<string, Map<NewsReactionEmoji, ReactionState>>;

function toCommentTree(rawComments: NewsCommentItem[]): NewsCommentNode[] {
  const commentsById = new Map<string, NewsCommentNode>();
  const roots: NewsCommentNode[] = [];

  for (const comment of rawComments) {
    commentsById.set(comment.id, {
      ...comment,
      replies: [],
    });
  }

  for (const comment of rawComments) {
    const current = commentsById.get(comment.id);
    if (!current) {
      continue;
    }

    if (comment.parentId && commentsById.has(comment.parentId)) {
      commentsById.get(comment.parentId)?.replies.push(current);
      continue;
    }

    roots.push(current);
  }

  return roots;
}

function getAuthorName(comment: NewsCommentItem): string {
  return getNewsCommentAuthorName({
    userId: comment.userId,
    userName: comment.userName ?? "",
    userEmail: comment.userEmail ?? null,
    wechatAvatar: comment.wechatAvatar ?? null,
    wechatNickname: comment.wechatNickname ?? null,
  });
}

function getAuthorBadge(comment: NewsCommentItem) {
  return getNewsCommentAuthorBadge({
    userId: comment.userId,
    userName: comment.userName ?? "",
    userEmail: comment.userEmail ?? null,
    wechatAvatar: comment.wechatAvatar ?? null,
    wechatNickname: comment.wechatNickname ?? null,
  });
}

export function createEmptyReactionState(): Map<NewsReactionEmoji, ReactionState> {
  return new Map<NewsReactionEmoji, ReactionState>(
    NEWS_REACTION_EMOJIS.map((emoji) => [emoji, { count: 0, reacted: false }]),
  );
}

export function mergeReactionSummary(
  reactions: {
    targetType: "news" | "comment";
    targetId: string;
    emoji: NewsReactionEmoji;
    count: number;
    isReactedByCurrentUser: boolean;
  }[],
) {
  const buckets = new Map<string, Map<NewsReactionEmoji, ReactionState>>();

  for (const reaction of reactions) {
    const key = getNewsReactionTargetKey(reaction);
    const bucket = buckets.get(key) ?? createEmptyReactionState();
    const current = bucket.get(reaction.emoji);

    if (current) {
      bucket.set(reaction.emoji, {
        count: reaction.count,
        reacted: reaction.isReactedByCurrentUser,
      });
    }
    buckets.set(key, bucket);
  }

  return buckets;
}

export function buildCommentThreads(rawComments: NewsCommentItem[]) {
  const tree = toCommentTree(rawComments);
  const authorNameById = new Map<string, string>();
  const authorIdById = new Map<string, string>();

  for (const comment of rawComments) {
    authorNameById.set(comment.id, getAuthorName(comment));
    authorIdById.set(comment.id, comment.userId);
  }

  const threads: CommentThreadItem[] = tree.map((root) => {
    const replies: FlatReplyItem[] = [];
    const rootAuthorId = root.userId;

    const collectReplies = (nodes: NewsCommentNode[]) => {
      for (const node of nodes) {
        replies.push({
          id: node.id,
          parentId: node.parentId ?? null,
          authorName: authorNameById.get(node.id) || "匿名用户",
          parentName: node.parentId ? authorNameById.get(node.parentId) : undefined,
          isParentRootAuthor: node.parentId
            ? authorIdById.get(node.parentId) === rootAuthorId
            : false,
          content: node.content,
          createdAt: node.createdAt,
          isRootAuthor: node.userId === rootAuthorId,
        });

        if (node.replies.length > 0) {
          collectReplies(node.replies);
        }
      }
    };

    collectReplies(root.replies);

    return {
      root,
      rootAuthorName: authorNameById.get(root.id) || "匿名用户",
      rootBadge: getAuthorBadge(root),
      rootCreatedLabel: new Date(root.createdAt).toISOString(),
      replies,
      rootAuthorId,
      commentIds: new Set([root.id, ...replies.map((item) => item.id)]),
    };
  });

  return {
    threads,
    authorNameById,
  };
}
